import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {type AccountType} from '#/state/classrooms/account'

/**
 * What the account that runs a classroom is called.
 *
 * A classroom has an owner — the account that made it, hands out the code,
 * answers the asks and can end it — and the app talked about that account as *the
 * school* everywhere. That was only ever true of one kind of owner: onboarding
 * records four kinds of account (`AccountType`), and a room may be run by an
 * organisation (a school) or by a creator (a tutor working on their own). A tutor
 * reading *and the school that runs it* about their own room is being told the app
 * does not know what they are, and a school reading *the tutor* about itself is
 * told something worse.
 *
 * So the word is asked for, from the kind of account that runs the room, and the
 * copy around it does not change: the sentence is the same sentence with the right
 * noun in it. Everything the app says about a room's owner should come from here
 * for the same reason — one fact, one place to say it — and the seats that answer
 * *everyone but me* are where the app has to name the owner at all.
 *
 * An owner whose kind this device cannot read — an older deployment, a profile
 * that has not loaded — is named the way every owner used to be named: *the
 * account that runs it*. That is not a guess about what they are, which is the
 * point: a learner, a parent and an unreadable profile are all described without
 * claiming to know.
 */
export function ownerNoun(
  _: (descriptor: MessageDescriptor) => string,
  accountType: AccountType | null,
): string {
  switch (accountType) {
    case 'organisation':
      return _(msg`the school that runs it`)
    case 'creator':
      return _(msg`the tutor who runs it`)
    default:
      return _(msg`the account that runs it`)
  }
}
